import type { EditorSDK, PageRef, RouterData } from '@wix/platform-editor-sdk';

import type { MembersPage } from '../../types';
import { log } from '../../utils/monitoring';
import { retryPromise } from '../../utils/promises';
import {
  APP_TOKEN,
  MEMBERS_LIST_APP_DEF_ID,
  MEMBERS_LIST_PAGE_ID,
  MEMBERS_PAGES_GROUP_NAME,
  SANTA_MEMBERS_APP_ID,
  SOCIAL_APPS_IDS,
  UNDELETABLE_PAGES,
} from '../constants';
import { removeMembersAreaPageByPageId } from '../platform-api/removeMembersAreaPage';
import * as pagesWrapper from '../wrappers/pages';
import * as pagesGroupWrapper from '../wrappers/pagesGroup';
import { isApplicationInstalled } from '../wrappers/tpa';
import * as menusService from './menus';
import * as routersService from './routers';
import { getMembersAreaRouters } from './routers';

async function setProtectedPage({ editorSDK, page }: { editorSDK: EditorSDK; page: MembersPage }) {
  const existingPageData = await pagesWrapper.getPageData({ editorSDK, pageRef: page.pageData.pageRef! });
  if (existingPageData && existingPageData.pageSecurity && !existingPageData.pageSecurity.requireLogin) {
    await pagesWrapper.updatePageData({
      editorSDK,
      pageRef: page.pageData.pageRef!,
      pageData: { pageSecurity: { requireLogin: true } },
    });
  }
}

function setProtectedPages({ editorSDK, pages }: { editorSDK: EditorSDK; pages: MembersPage[] }) {
  return Promise.all(pages.map((page) => setProtectedPage({ editorSDK, page })));
}

function addPagesToPagesGroup({ editorSDK, pages }: { editorSDK: EditorSDK; pages: MembersPage[] }) {
  return Promise.all(
    pages.map((page) =>
      retryPromise(
        () => pagesGroupWrapper.addPageToGroup(editorSDK, MEMBERS_PAGES_GROUP_NAME, page.pageData.pageRef?.id!),
        { delayMs: 200, message: 'Retry adding page to a pages group' },
      ),
    ),
  );
}

function updatePageWithManagingAppDefId({ editorSDK, pageRef }: { editorSDK: EditorSDK; pageRef: PageRef }) {
  return pagesWrapper.updatePageData({ editorSDK, pageRef, pageData: { managingAppDefId: SANTA_MEMBERS_APP_ID } });
}

async function connectPagesToMembers({ editorSDK, pages }: { editorSDK: EditorSDK; pages: MembersPage[] }) {
  const { publicRouter, privateRouter } = await routersService.getMembersAreaRouters(editorSDK);
  const privatePages = pages.filter((page) => page.pageData.isPrivate);
  const publicPages = pages.filter((page) => !page.pageData.isPrivate);
  const includesPrivatePages = privatePages.length > 0;
  const includesPublicPages = publicPages.length > 0;

  await Promise.all(
    [
      includesPrivatePages &&
        routersService.connectPagesToRouter({ editorSDK, pages: privatePages, router: privateRouter }),
      includesPublicPages &&
        routersService.connectPagesToRouter({ editorSDK, pages: publicPages, router: publicRouter }),
      includesPrivatePages && setProtectedPages({ editorSDK, pages: privatePages }),
    ].filter((p) => !!p) as Promise<void[]>[],
  );

  try {
    await addPagesToPagesGroup({ editorSDK, pages });
  } catch (error) {
    log('Failed to add new page to the pages group, SOSP will not be visible in the page', {
      extra: { error: (error as Error).toString(), stack: (error as Error)?.stack },
    });
  }

  return menusService.connectPagesToMenus({ editorSDK, pages });
}

async function setStateForPages(editorSDK: EditorSDK) {
  const applicationPages = await editorSDK.document.pages.getApplicationPages(APP_TOKEN);

  const readOnly: PageRef[] = [];
  const deletable: PageRef[] = [];

  applicationPages.forEach((pageData) => {
    const pageRef = { id: pageData.id, type: 'DESKTOP' };
    if (UNDELETABLE_PAGES && UNDELETABLE_PAGES.includes(pageData.tpaPageId!)) {
      readOnly.push(pageRef as PageRef);
    } else {
      deletable.push(pageRef as PageRef);
    }
  });

  await editorSDK.document.pages.setState('', {
    state: {
      readOnly,
      deletable,
    },
  });
  return editorSDK.document.application.reloadManifest();
}

async function getPageByIntegrationApp({
  editorSDK,
  app,
}: {
  editorSDK: EditorSDK;
  app: { pageId: string; appDefinitionId: string };
}) {
  const allPages = await pagesWrapper.getAllPages({ editorSDK });

  return allPages.find((page) => page.tpaPageId === app.pageId && page.appDefinitionId === app.appDefinitionId);
}

async function getAllMembersPagesRefs({ editorSDK }: { editorSDK: EditorSDK }) {
  const { publicRouter = {}, privateRouter = {} } = await routersService.getMembersAreaRouters(editorSDK);
  const allMAPages = [...((publicRouter as RouterData).pages || []), ...((privateRouter as RouterData).pages || [])];

  return allMAPages.map((page) => page.pageRef);
}

async function hasSocialPages(editorSDK: EditorSDK) {
  for (const appDefinitionId of SOCIAL_APPS_IDS) {
    if (await isApplicationInstalled({ editorSDK, appDefinitionId })) {
      return true;
    }
  }

  const { publicRouter } = await getMembersAreaRouters(editorSDK);
  return Object.keys(publicRouter?.config?.patterns ?? {}).length > 0;
}

const removeMembersList = async (editorSDK: EditorSDK, shouldDisableTransaction?: boolean) => {
  const pageData = await getPageByIntegrationApp({
    editorSDK,
    app: { pageId: MEMBERS_LIST_PAGE_ID, appDefinitionId: MEMBERS_LIST_APP_DEF_ID },
  });

  await updatePageWithManagingAppDefId({ editorSDK, pageRef: { id: pageData?.id!, type: 'DESKTOP' } });

  await removeMembersAreaPageByPageId({
    appDefinitionId: MEMBERS_LIST_APP_DEF_ID,
    pageId: MEMBERS_LIST_PAGE_ID,
    editorSDK,
    shouldDisableTransaction,
  });
};

export {
  connectPagesToMembers,
  getAllMembersPagesRefs,
  getPageByIntegrationApp,
  hasSocialPages,
  removeMembersList,
  setStateForPages,
  updatePageWithManagingAppDefId,
};
